import React, { useEffect, useRef } from "react";

import { UseFormRegister, UseFormSetValue, UseFormWatch } from "react-hook-form";

import { TrashIcon } from "@heroicons/react/outline";


import { SelectOption, StyledSelectInput } from "../Shared/StyledInputs/StyledSelectInput";
import { StyledMultiSelectInput } from "../Shared/StyledInputs/StyledMultiSelectInput";
import { useMarketingGroups } from "../../data/marketingGroup/useMarketingGroups";
import useForms from "../../data/useForms";

export function ConditionRow({
  conditionIndex,
  register,
  onDelete,
  setValue,
  watch,
}: {
  conditionIndex: number;
  register: UseFormRegister<any>;
  onDelete: (index: number) => void;
  setValue: UseFormSetValue<any>;
  watch: UseFormWatch<any>;
}) {
  const { marketingGroups } = useMarketingGroups();
  const { forms } = useForms();

  // Watch values for initialization
  const currentValue = watch(`conditions.${conditionIndex}.value`);
  const currentMultiValue = watch(`conditions.${conditionIndex}.multiValue`);
  const currentFieldName = watch(`conditions.${conditionIndex}.fieldName`);
  const type = watch(`conditions.${conditionIndex}.type`);
  const trigger = watch('trigger');

  // Use refs to track previous values
  const prevTrigger = useRef(trigger);
  const prevFieldName = useRef(currentFieldName);
  const isInitialized = useRef(false);

  // Clear values when trigger changes (but not on initial load)
  useEffect(() => {
    if (isInitialized.current && prevTrigger.current !== trigger) {
      // Clear field name and values when trigger changes
      setValue(`conditions.${conditionIndex}.fieldName`, '');
      setValue(`conditions.${conditionIndex}.value`, '');
      setValue(`conditions.${conditionIndex}.multiValue`, []);
    }
    prevTrigger.current = trigger;
  }, [trigger, conditionIndex, setValue]);

  // Clear values when field name changes (but not on initial load)
  useEffect(() => {
    if (isInitialized.current && prevFieldName.current !== currentFieldName) {
      // Clear values when field name changes
      setValue(`conditions.${conditionIndex}.value`, '');
      setValue(`conditions.${conditionIndex}.multiValue`, []);
    }
    prevFieldName.current = currentFieldName;
  }, [currentFieldName, conditionIndex, setValue]);

  // Handle initialization and validation
  useEffect(() => {
    // Validate and set default field name for types that use field-based selection
    if (parseInt(type || "0") === 0) {
      const fieldOptions = getConditionFieldsForTrigger(parseInt(trigger || "0"));
      if (fieldOptions.length > 0 && (!currentFieldName || !fieldOptions.some(opt => opt.value === currentFieldName))) {
        setValue(`conditions.${conditionIndex}.fieldName`, fieldOptions[0].value);
      }

      // Initialize multiValue array if it doesn't exist (for default case multi-select)
      if (!currentMultiValue) {
        setValue(`conditions.${conditionIndex}.multiValue`, []);
      }

      // If we have a comma-separated value but multiValue is empty or doesn't match, sync them
      if (currentValue && currentValue.trim() !== '') {
        const valueArray = currentValue.split(',').filter((val: string) => val.trim() !== '');
        const multiValueArray = currentMultiValue || [];

        // Only update if arrays are different (to avoid infinite loops)
        const arraysAreDifferent = valueArray.length !== multiValueArray.length ||
          !valueArray.every((val: string, index: number) => val === multiValueArray[index]);

        if (arraysAreDifferent) {
          setValue(`conditions.${conditionIndex}.multiValue`, valueArray);
        }
      } else if ((!currentValue || currentValue.trim() === '') && currentMultiValue && currentMultiValue.length > 0) {
        // If no value but multiValue has items, clear multiValue
        setValue(`conditions.${conditionIndex}.multiValue`, []);
      }
    }

    // Mark as initialized after first run
    if (!isInitialized.current) {
      isInitialized.current = true;
    }
  }, [currentValue, type, trigger, currentFieldName, conditionIndex, setValue, currentMultiValue]);

  const getTypeOptions = (trigger: number) => {
    const defaultOpts = [
      { label: "Field", value: "0" },
      { label: "Marketing Group", value: "1" },
      { label: "Contact Type", value: "2" },
      { label: "Lead Score", value: "3" },
    ]

    switch (trigger) {
      case 3:
        return [
          ...defaultOpts,
          { label: "Form Name", value: "4" },
        ];
      default:
        return defaultOpts;
    }
  }

  const getConditionFieldsForTrigger = (trigger: number) => {
    let opts: SelectOption[] = [];

    switch (trigger) {
      case 1:
        opts = [
          { label: "Valuation Type", value: "valuationType" },
          { label: "Valuation Method", value: "valuationSource" },
          { label: "Status", value: "status" },
          { label: "Property Type", value: "propertyType" }
        ]

        break;
      case 2:
        opts = [
          { label: "Require Mortgage", value: "requireMortgage" }
        ]

        break;
      case 3:
        opts = [
          { label: "Status", value: "status" },
        ]

        break;
      case 4:
        opts = [
          { label: "Site Name", value: "sourceName" },
          { label: "Sales Enabled", value: "salesEnabled" },
          { label: "Lettings Enabled", value: "lettingsEnabled" }
        ]

        break;
      default:
        opts = [
          { label: "No fields available for this trigger", value: "" }
        ]
    }

    return opts;
  }

  const getConditionFieldValues = (fieldName: string) => {
    let opts: SelectOption[] = [];

    switch (fieldName) {
      case "status":
        if (watch('trigger') === "1") {
          opts = [
            { label: "New", value: "0" },
            { label: "Lost", value: "1" },
            { label: "Contact Made", value: "2" },
            { label: "Booked", value: "3" },
            { label: "Appraisal Sent", value: "4" },
            { label: "Won", value: "5" },
            { label: "Updated", value: "6" }
          ]
        } else {
          opts = [
            { label: "New", value: "0" },
            { label: "Contact Made", value: "1" },
            { label: "Confirmed", value: "2" },
            { label: "Completed", value: "3" }
          ]
        }
        break;
      case "valuationType":
        opts = [
          { label: "Combined", value: "0" },
          { label: "Sales", value: "1" },
          { label: "Lettings", value: "2" },
        ]
        break;
      case "propertyType":
        opts = [
          { label: "Detached", value: "0" },
          { label: "Semi-Detached", value: "1" },
          { label: "Terraced", value: "2" },
          { label: "Flat/Maisonette", value: "3" },
          { label: "Other", value: "4" }
        ]
        break;
      case "valuationSource":
        opts = [
          { label: "Virtual", value: "0" },
          { label: "Instant", value: "1" },
          { label: "In Person", value: "2" },
          { label: "Portal", value: "3" }
        ]
        break;
      case "sourceName":
        opts = [
          { label: "Zoopla", value: "zoopla" },
          { label: "Rightmove", value: "rightmove" },
          { label: "OnTheMarket", value: "onthemarket" },
          { label: "Website", value: "website" }
        ]
        break;
      case "salesEnabled":
      case "lettingsEnabled":
        opts = [
          { label: "Enabled", value: "true" },
          { label: "Disabled", value: "false" }
        ]
        break;
      case "requireMortgage":
        opts = [
          { label: "Yes", value: "I require a mortgage" },
          { label: "No", value: "I do not require a mortgage" }
        ]
        break;
    }

    return opts;
  }

  const getInputForType = (type: number) => {
    switch (type) {
      case 1:
        return (
          <div className="col-span-6 flex items-center">
            <StyledMultiSelectInput
              classes="!w-full"
              label="Marketing Groups"
              registerName={`conditions.${conditionIndex}.marketingGroupIds`}
              register={register}
              onChange={(val) => {
                setValue(`conditions.${conditionIndex}.value`, val.join(','));
              }}
              options={[
                ...marketingGroups.data?.data.map((group) => ({
                  value: group.id,
                  label: group.name
                })) ?? []]}
              setValue={setValue}
              watch={watch}
            />
          </div>
        )
      case 2:
        return (
          <div className="col-span-6 flex items-center">
            <StyledSelectInput
              classes="!w-full"
              label="Contact Type"
              registerName={`conditions.${conditionIndex}.value`}
              register={register}
              options={[
                { value: "", label: "Choose Type..." },
                { value: "sales", label: "Sales" },
                { value: "lettings", label: "Lettings" },
                { value: "combined", label: "Combined" },
              ]}
              setValue={setValue}
              watch={watch}
            />
          </div>
        )
      case 3:
        return (
          <div className="col-span-6 flex items-center">
            <StyledSelectInput
              classes="!w-full"
              label="Star Rating"
              registerName={`conditions.${conditionIndex}.value`}
              register={register}
              options={[
                { value: "1", label: "Above 0 Star" },
                { value: "2", label: "Above 1 Star" },
                { value: "3", label: "Above 2 Star" },
                { value: "4", label: "Above 3 Star" },
                { value: "5", label: "Above 4 Star" },
              ]}
              setValue={setValue}
              watch={watch}
            />
          </div>
        )
      case 4:
        return (
          <div className="col-span-6 flex items-center">
            <StyledMultiSelectInput
              classes="!w-full"
              label="Forms"
              registerName={`conditions.${conditionIndex}.formIds`}
              register={register}
              onChange={(val) => {
                setValue(`conditions.${conditionIndex}.value`, val.join(','));
              }}
              options={[
                ...forms.data?.map((form: any) => ({
                  value: form.id,
                  label: form.title
                })) ?? []]}
              setValue={setValue}
              watch={watch}
            />
          </div>
        )
      default:
        return (<div className="col-span-6 flex items-center space-x-2 justify-between">
          {watch('trigger') !== "5" ?
            <>
              <StyledSelectInput
                label="Field Name"
                registerName={`conditions.${conditionIndex}.fieldName`}
                register={register}
                options={getConditionFieldsForTrigger(parseInt(watch(`trigger`) || "0"))}
                setValue={setValue}
                watch={watch}
                classes="w-full"
              />
              <input type="hidden" {...register(`conditions.${conditionIndex}.operator`)} />
              <p className="mb-2 self-end text-gray-500">IS</p>
              <StyledMultiSelectInput
                label="Value"
                registerName={`conditions.${conditionIndex}.multiValue`}
                register={register}
                onChange={(val) => {
                  setValue(`conditions.${conditionIndex}.value`, val.join(','));
                }}
                options={getConditionFieldValues(watch(`conditions.${conditionIndex}.fieldName`)) || []}
                setValue={setValue}
                watch={watch}
                classes="w-full"
                noneSelectedText="Choose Value(s)..."
              />
            </>
            :
            <div className="w-full text-gray-600 text-sm text-center">No filter fields available for this trigger </div>
          }
        </div>
        );
    }
  }

  return (
    <div className="grid grid-cols-10 gap-2 py-4 rounded-md border border-gray-100">
      <div className="flex items-center justify-center">
        <p className="text-gray-500 font-bold text-base">{conditionIndex + 1}</p>
      </div>
      <input type="hidden" {...register(`conditions.${conditionIndex}.operator`)} />
      <input type="hidden" {...register(`conditions.${conditionIndex}.nurtureCampaignRuleId`)} />
      <input type="hidden" {...register(`conditions.${conditionIndex}.id`)} />
      <input type="hidden" {...register(`conditions.${conditionIndex}.fieldName`)} />
      <div className="col-span-2">
        <StyledSelectInput
          label="Type"
          registerName={`conditions.${conditionIndex}.type`}
          onChange={(val) => {
            // Always clear multiValue when changing type
            setValue(`conditions.${conditionIndex}.multiValue`, []);

            if (val === "1") {
              setValue(`conditions.${conditionIndex}.value`, "");
            } else if (val === "2") {
              setValue(`conditions.${conditionIndex}.value`, "");
            } else if (val === "3") {
              setValue(`conditions.${conditionIndex}.value`, "1");
              setValue(`conditions.${conditionIndex}.operator`, ">=");
            } else if (val === "4") {
              setValue(`conditions.${conditionIndex}.value`, "");
            } else if (val === "0") {
              // Default case - reset value
              setValue(`conditions.${conditionIndex}.value`, "");
            }

            if (val !== "0") {
              setValue(`conditions.${conditionIndex}.fieldName`, "");
            }

            if (val !== "3" && watch(`conditions.${conditionIndex}.operator`) === ">=") {
              setValue(`conditions.${conditionIndex}.operator`, "=");
            }
          }}
          register={register}
          options={getTypeOptions(parseInt(watch(`trigger`) || "0"))}
          setValue={setValue}
          watch={watch}
        />
      </div>
      {getInputForType(parseInt(watch(`conditions.${conditionIndex}.type`) || "0"))}
      <div className="flex items-center justify-center">
        <button
          type="button"
          onClick={() => onDelete(conditionIndex)}
          className="mx-2 text-gray-400 hover:text-red-600"
        >
          <span className="sr-only">Delete</span>
          <TrashIcon className="h-6 w-6" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}